import React from "react";
import { format } from "date-fns";
import { captureException } from "@sentry/react";

export function toGreeklish(text: string) {
  function fixCase(text, mirror) {
    if (grCaps[mirror.charAt(0)]) {
      if (mirror.length == 1 || grCaps[mirror.charAt(1)]) {
        return text.toUpperCase();
      } else {
        return text.charAt(0).toUpperCase() + text.substr(1);
      }
    } else {
      return text;
    }
  }

  function stringToSet(s) {
    var o = {};
    for (var i = 0; i < s.length; i++) {
      o[s.charAt(i)] = 1;
    }
    return o;
  }

  var grCaps = stringToSet("ΑΆΒΓΔΕΈΖΗΉΘΙΊΪΚΛΜΝΞΟΌΠΡΣΤΥΎΫΦΧΨΩΏ");
  var replacements = [
    { greek: "αι", greeklish: "ai" },
    { greek: "αί", greeklish: "ai" },
    { greek: "οι", greeklish: "oi" },
    { greek: "οί", greeklish: "oi" },
    { greek: "ου", greeklish: "ou" },
    { greek: "ού", greeklish: "ou" },
    { greek: "ει", greeklish: "ei" },
    { greek: "εί", greeklish: "ei" },
    { greek: "αυ", fivi: 1 },
    { greek: "αύ", fivi: 1 },
    { greek: "ευ", fivi: 1 },
    { greek: "εύ", fivi: 1 },
    { greek: "ηυ", fivi: 1 },
    { greek: "ηύ", fivi: 1 },
    { greek: "ντ", greeklish: "nt" },
    { greek: "μπ", bi: 1 },
    { greek: "τσ", greeklish: "ts" },
    { greek: "τς", greeklish: "ts" },
    { greek: "ΤΣ", greeklish: "ts" },
    { greek: "τζ", greeklish: "tz" },
    { greek: "γγ", greeklish: "ng" },
    { greek: "γκ", greeklish: "gk" },
    { greek: "θ", greeklish: "th" },
    { greek: "χ", greeklish: "ch" },
    { greek: "ψ", greeklish: "ps" },
  ];
  // Remove extraneus array element
  if (!replacements[replacements.length - 1]) replacements.pop();
  // Enchance replacements
  for (var i = 0, replacement; (replacement = replacements[i]); i++) {
    replacements[replacement.greek] = replacement;
  }
  // Append single letter replacements
  var grLetters = "αάβγδεέζηήθιίϊΐκλμνξοόπρσςτυύϋΰφχψωώ";
  var engLetters = "aavgdeezii.iiiiklmnxooprsstyyyyf..oo";
  for (var i = 0; i < grLetters.length; i++) {
    if (!replacements[grLetters.charAt(i)]) {
      replacements.push({
        greek: grLetters.charAt(i),
        greeklish: engLetters.charAt(i),
      });
    }
  }
  // Enchance replacements, build expression
  var expression = [];
  for (var i = 0, replacement; (replacement = replacements[i]); i++) {
    replacements[replacement.greek] = replacement;
    expression[i] = replacement.greek;
  }
  expression = new RegExp(expression.join("|"), "gi");
  // Normalize look-alike chars that the case-insensitive regex matches but are not in replacements
  text = normalizeGreekLookAlikes(text);
  // Replace greek with greeklsh
  var greekSet = stringToSet(grLetters);
  var viSet = stringToSet("αάβγδεέζηήιίϊΐλμνοόρυύϋΰωώ");
  text = text.replace(expression, function ($0, index) {
    var replacement = replacements[$0.toLowerCase()];
    // Unknown look-alike char: report it to Sentry (not shown to the user) so it can be added to greekLookAlikes, keep it as is
    if (!replacement) {
      var codePoints = Array.from($0)
        .map(
          (c) =>
            "U+" + c.codePointAt(0)!.toString(16).toUpperCase().padStart(4, "0"),
        )
        .join(" ");
      captureException(
        new Error(
          `toGreeklish: no replacement for "${$0}" (${codePoints}), add it to greekLookAlikes`,
        ),
      );
      return $0;
    }
    if (replacement.bi) {
      var bi =
        greekSet[text.charAt(index - 1).toLowerCase()] &&
        greekSet[text.charAt(index + 2).toLowerCase()]
          ? "mp"
          : "b";
      return fixCase(bi, $0);
    } else if (replacement.fivi) {
      var c1 = replacements[$0.charAt(0).toLowerCase()].greeklish;
      var c2 = viSet[text.charAt(index + 2).toLowerCase()] ? "v" : "f";
      return fixCase(c1 + c2, $0);
    } else {
      return fixCase(
        replacement.greeklish,
        $0 + text.charAt(index + $0.length),
      );
    }
  });
  return text;
}

/* Replaces look-alike chars (e.g. micro sign µ from Word/PDF copy-paste) with the actual greek letters
 * Keys are escaped on purpose: some of them (e.g. U+1FBE) are silently normalized by editors to the target letter
 */
const greekLookAlikes: Record<string, string> = {
  "\u00B5": "μ", // MICRO SIGN
  "\u0345": "ι", // COMBINING GREEK YPOGEGRAMMENI
  "\u1FBE": "ι", // GREEK PROSGEGRAMMENI
  "\u03D0": "β", // GREEK BETA SYMBOL
  "\u03D1": "θ", // GREEK THETA SYMBOL
  "\u03D5": "φ", // GREEK PHI SYMBOL
  "\u03D6": "π", // GREEK PI SYMBOL
  "\u03F0": "κ", // GREEK KAPPA SYMBOL
  "\u03F1": "ρ", // GREEK RHO SYMBOL
  "\u03F5": "ε", // GREEK LUNATE EPSILON SYMBOL
};
const greekLookAlikesRegex = new RegExp(
  `[${Object.keys(greekLookAlikes).join("")}]`,
  "g",
);

export function normalizeGreekLookAlikes(text: string) {
  return text.replace(greekLookAlikesRegex, (c) => greekLookAlikes[c]);
}

/* A function where an object with params is serialized into request type string.
 * For example {param1: value1, param2: value2} => ?param1=value1&param2=value2
 */
export function paramBuilder(params: Object) {
  let paramString: string = "?";
  let paramArray: Array<string> = [];

  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      paramArray.push(`${key}=${value}`);
    }
  });

  paramString += paramArray.join("&");

  return paramString;
}

export function dateCheckAndFormat(
  date: string | Date,
  formatString: string = "dd/MM/yyyy",
): string {
  return typeof date === "string" ? date : format(date, formatString);
}

export function numberOrUndefined(value: unknown): number | undefined {
  return value !== null && value !== undefined && value !== ""
    ? Number(value)
    : undefined;
}

export function formatNumber(value: string) {
  if (!value) return value;
  const num = Number(value);
  return num.toLocaleString("fullwide", {
    useGrouping: false,
    maximumFractionDigits: 2,
  });
}

const BIGINT_MAX = 9223372036854775807n;

export function blockNonDigit(e: React.KeyboardEvent): void {
  if (e.ctrlKey || e.metaKey) return;
  const allowed = [
    "Backspace", "Delete", "Tab", "Escape", "Enter",
    "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End",
  ];
  if (allowed.includes(e.key)) return;
  if (!/^\d$/.test(e.key)) e.preventDefault();
}

export function validateBigIntRange(value: string | undefined): true | string {
  if (!value) return true;
  if (!/^\d+$/.test(value)) return "Επιτρέπονται μόνο ακέραιοι αριθμοί";
  try {
    return BigInt(value) <= BIGINT_MAX
      ? true
      : "Ο αριθμός υπερβαίνει το μέγιστο επιτρεπτό όριο";
  } catch {
    return "Μη έγκυρος αριθμός";
  }
}

export function chamberBackUrl(
  url: string | null,
  id: string | null = null,
): {
  toPage: string;
  toTitle: string;
} {
  let toPage: string;
  let toTitle: string;
  switch (url) {
    case "activeOwnCertificateApplications":
      toPage = "/chamber/activeOwnCertificateApplications";
      toTitle = "Μετάβαση στις Αιτήσεις σε εξέλιξη";
      break;
    case "activeChamberCertificateApplicationsPaid":
      toPage = "/chamber/activeChamberCertificateApplicationsPaid";
      toTitle = "Μετάβαση στις Αιτήσεις που δεν έχουν ανατεθεί ακόμη";
      break;
    case "activeChamberCertificateApplicationsUnpaid":
      toPage = "/chamber/activeChamberCertificateApplicationsUnpaid";
      toTitle = "Μετάβαση στις Αιτήσεις με οποιαδήποτε εκκρεμότητα πληρωμής";
      break;
    case "getCertsByGemi":
      toPage = "/chamber/getCertsByGemi";
      toTitle = "Μετάβαση στην Γρήγορη Αναζήτηση με Αρ. ΓΕΜΗ";
      break;
    case "pastOwnCertificateApplications":
      toPage = "/chamber/pastOwnCertificateApplications";
      toTitle = "Μετάβαση στο Αρχείο Εκπληρωμένων Υποθέσεων";
      break;
    case "activeOwnOnlyApplications":
      toPage = "/chamber/activeOwnOnlyApplications";
      toTitle = "Μετάβαση στις Αιτήσεις που απαιτούν δική μου ενέργεια";
      break;
    case "activeChamberApplicationsPaid":
      toPage = "/chamber/activeChamberApplicationsPaid";
      toTitle = "Μετάβαση στις Αιτήσεις που δεν έχουν ανατεθεί ακόμη";
      break;
    case "getAppsByGemi":
      toPage = "/chamber/getAppsByGemi";
      toTitle = "Μετάβαση στην Γρήγορη Αναζήτηση με Αρ. ΓΕΜΗ";
      break;
    case "activeChamberApplicationsUnpaid":
      toPage = "/chamber/activeChamberApplicationsUnpaid";
      toTitle = "Μετάβαση στις Αιτήσεις με οποιαδήποτε εκκρεμότητα πληρωμής";
      break;
    case "activeOwnApplications":
      toPage = "/chamber/activeOwnApplications";
      toTitle = "Μετάβαση στις Αιτήσεις σε εξέλιξη";
      break;
    case "inactiveApplications":
      toPage = "/chamber/inactiveApplications";
      toTitle = "Μετάβαση στις Αιτήσεις ανατεθειμένες σε ανενεργούς χρήστες";
      break;
    case "inactiveCertificateApplications":
      toPage = "/chamber/inactiveCertificateApplications";
      toTitle = "Μετάβαση στις Αιτήσεις Πιστοποιητικών ανατεθειμένες σε ανενεργούς χρήστες";
      break;
    case "pastOwnApplications":
      toPage = "/chamber/pastOwnApplications";
      toTitle = "Μετάβαση στο Αρχείο Εκπληρωμένων Υποθέσεων";
      break;
    case "sampleAssignedCases":
      toPage = `/sampleInspection/case/dashboard`;
      toTitle = "Μετάβαση στον Έλεγχο επιχειρήσεων που μου έχουν ανατεθεί";
      break;
    case "chamberAdminSampleInspections":
      toPage = "/sampleInspection/sample/chamberDashboard";
      toTitle = "Μετάβαση στη Διαχείριση δειγματοληπτικών ελέγχων από Υ.ΓΕ.ΜΗ.";
      break;
    case "initiatorsSampleInspections":
      toPage = "/sampleInspection/sample/initiatorsDashboard";
      toTitle =
        "Μετάβαση στις Υποθέσεις δειγματοληπτικών ελέγχων (όλων των Υ.Γ.Ε.ΜΗ.)";
      break;
    case "initiatorsSampleDetails":
      toPage = `/sampleInspection/sample/${id}/initiatorsDetails`;
      toTitle = "Μετάβαση στην προβολή του Δείγματος Ελέγχου";
      break;
    default:
      toPage = "/chamber/dashboard";
      toTitle = "Μετάβαση στον Πίνακα Ελέγχου";
      break;
  }
  return { toPage, toTitle };
}
